import { c as _c } from "react/compiler-runtime";
import { Footer, Navbar } from '@/components/landing/LandingPage';
import { SeoHead } from '@/components/landing/SeoHead';
import { LandingLocaleProvider, useLandingLocale } from '@/components/landing/useLandingLocale';
import { blogPosts, getBlogPostBySlug, type BlogPost, type BlogPostBlock } from '@/data/blogPosts';
import { ArrowLeft } from 'lucide-react';
import type { GetStaticPaths, GetStaticProps } from 'next';
import Link from 'next/link';
const PRIMARY = '#2468ff';
function formatDate(iso: string, locale: 'en' | 'fr') {
  return new Date(iso + 'T12:00:00').toLocaleDateString(locale === 'fr' ? 'fr-FR' : 'en-US', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
}
function CoverArt(t0) {
  const $ = _c(11);
  const {
    post
  } = t0;
  let t1;
  if ($[0] !== post.cover.tint) {
    t1 = {
      backgroundColor: post.cover.tint
    };
    $[0] = post.cover.tint;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = `radial-gradient(circle at 30% 30%, ${post.cover.color}44, transparent 60%)`;
  let t3;
  if ($[2] !== t2) {
    t3 = <div className="absolute inset-0 opacity-70" style={{
      background: t2
    }} aria-hidden={true} />;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      backgroundColor: "white"
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== post.cover.emoji) {
    t5 = <div className="relative flex h-28 w-28 items-center justify-center rounded-3xl text-6xl shadow-[0_24px_60px_-16px_rgba(0,0,0,0.25)]" style={t4}>{post.cover.emoji}</div>;
    $[5] = post.cover.emoji;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t1 || $[8] !== t3 || $[9] !== t5) {
    t6 = <div className="relative flex aspect-[16/8] w-full items-center justify-center overflow-hidden rounded-3xl" style={t1}>{t3}{t5}</div>;
    $[7] = t1;
    $[8] = t3;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
}
function Block(t0) {
  const $ = _c(11);
  const {
    block
  } = t0;
  if (block.type === "paragraph") {
    let t1;
    if ($[0] !== block.text) {
      t1 = <p className="text-[17px] leading-[1.7] text-neutral-700">{block.text}</p>;
      $[0] = block.text;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (block.type === "heading") {
    let t1;
    if ($[2] !== block.text) {
      t1 = <h2 className="mt-8 text-[26px] font-semibold leading-tight tracking-tight text-neutral-900">{block.text}</h2>;
      $[2] = block.text;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    return t1;
  }
  if (block.type === "list") {
    let t1;
    if ($[4] !== block.items) {
      t1 = block.items.map(_temp);
      $[4] = block.items;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    let t2;
    if ($[6] !== t1) {
      t2 = <ul className="ml-5 list-disc space-y-2 text-[17px] leading-[1.7] text-neutral-700 marker:text-neutral-400">{t1}</ul>;
      $[6] = t1;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    return t2;
  }
  if (block.type === "quote") {
    let t1;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = {
        borderColor: PRIMARY
      };
      $[8] = t1;
    } else {
      t1 = $[8];
    }
    let t2;
    if ($[9] !== block.text) {
      t2 = <blockquote className="my-2 rounded-2xl border-l-4 bg-white p-5 text-[18px] italic leading-relaxed text-neutral-800" style={t1}>“{block.text}”</blockquote>;
      $[9] = block.text;
      $[10] = t2;
    } else {
      t2 = $[10];
    }
    return t2;
  }
  return null;
}
function _temp(item, i) {
  return <li key={i}>{item}</li>;
}
function BackgroundBlobs() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <><div className="pointer-events-none absolute -top-24 left-1/2 h-[420px] w-[860px] -translate-x-1/2 rounded-full opacity-50 blur-3xl" style={{
        background: `radial-gradient(closest-side, ${PRIMARY}22, transparent 70%)`
      }} aria-hidden={true} /></>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function Inner(t0) {
  const $ = _c(46);
  const {
    post
  } = t0;
  const {
    locale,
    t
  } = useLandingLocale();
  const copy = post[locale];
  const category = post.category[locale];
  const t1 = `${copy.title} — ${t.meta.blogPostSuffix}`;
  let t2;
  if ($[0] !== copy.excerpt || $[1] !== t1) {
    t2 = <SeoHead title={t1} description={copy.excerpt} />;
    $[0] = copy.excerpt;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Navbar />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <BackgroundBlobs />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ArrowLeft className="h-3.5 w-3.5" />;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t.blog.backToBlog) {
    t6 = <Link href="/blog" className="inline-flex items-center gap-1.5 text-[13px] font-medium text-neutral-500 transition-colors hover:text-black">{t5}{t.blog.backToBlog}</Link>;
    $[6] = t.blog.backToBlog;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      backgroundColor: `${PRIMARY}14`,
      color: PRIMARY
    };
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== category) {
    t8 = <span className="rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider" style={t7}>{category}</span>;
    $[9] = category;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== locale || $[12] !== post.date) {
    t9 = formatDate(post.date, locale);
    $[11] = locale;
    $[12] = post.date;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t9) {
    t10 = <span>{t9}</span>;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <span aria-hidden={true}>·</span>;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== post.readMinutes || $[18] !== t.blog.minRead) {
    t12 = <span>{post.readMinutes} {t.blog.minRead}</span>;
    $[17] = post.readMinutes;
    $[18] = t.blog.minRead;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== t10 || $[21] !== t12 || $[22] !== t8) {
    t13 = <div className="mt-6 flex items-center gap-3 text-[12px] text-neutral-500">{t8}{t10}{t11}{t12}</div>;
    $[20] = t10;
    $[21] = t12;
    $[22] = t8;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== copy.title) {
    t14 = <h1 className="mt-4 text-[36px] font-semibold leading-[1.1] tracking-tight text-neutral-900 sm:text-[44px]">{copy.title}</h1>;
    $[24] = copy.title;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== post) {
    t15 = <div className="mt-8"><CoverArt post={post} /></div>;
    $[26] = post;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== copy.body) {
    t16 = copy.body.map(_temp2);
    $[28] = copy.body;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== t16) {
    t17 = <div className="mt-10 flex flex-col gap-5">{t16}</div>;
    $[30] = t16;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <ArrowLeft className="h-4 w-4" />;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== t.blog.backCta) {
    t19 = <div className="mt-14 flex justify-center"><Link href="/blog" className="inline-flex items-center gap-2 rounded-full bg-black px-6 py-3 text-[15px] font-medium text-white transition-transform hover:-translate-y-0.5">{t18}{t.blog.backCta}</Link></div>;
    $[33] = t.blog.backCta;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] !== t13 || $[36] !== t14 || $[37] !== t15 || $[38] !== t17 || $[39] !== t19 || $[40] !== t6) {
    t20 = <section className="relative overflow-hidden pb-20 pt-10 sm:pt-16">{t4}<article className="relative mx-auto max-w-[760px] px-6">{t6}{t13}{t14}{t15}{t17}{t19}</article></section>;
    $[35] = t13;
    $[36] = t14;
    $[37] = t15;
    $[38] = t17;
    $[39] = t19;
    $[40] = t6;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <Footer />;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== t2 || $[44] !== t20) {
    t22 = <main className="min-h-screen bg-[#f6f6f6] text-neutral-900 antialiased">{t2}{t3}{t20}{t21}</main>;
    $[43] = t2;
    $[44] = t20;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  return t22;
}
function _temp2(block, i) {
  return <Block key={i} block={block} />;
}
type Props = {
  post: BlogPost;
};
export default function BlogPostPage(t0) {
  const $ = _c(2);
  const {
    post
  } = t0;
  let t1;
  if ($[0] !== post) {
    t1 = <LandingLocaleProvider><Inner post={post} /></LandingLocaleProvider>;
    $[0] = post;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: blogPosts.map(p => ({
      params: {
        slug: p.slug
      }
    })),
    fallback: false
  };
};
export const getStaticProps: GetStaticProps<Props> = async ctx => {
  const slug = ctx.params?.slug as string;
  const post = getBlogPostBySlug(slug);
  if (!post) {
    return {
      notFound: true
    };
  }
  return {
    props: {
      post
    }
  };
};